import { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";

const SuccessPopup = ({
  message,
  isOpen,
  onClose,
  autoHideDuration = 1500,
  error = false,
  highPriority = false,
}) => {
  const onCloseRef = useRef(onClose);
  const lastToastSignatureRef = useRef(null);

  const effectiveAutoHideDuration = useMemo(() => {
    if (highPriority) {
      return 1000;
    }

    if (error) {
      return Math.max(autoHideDuration, 3000);
    }

    return autoHideDuration;
  }, [autoHideDuration, error, highPriority]);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen || !message) {
      lastToastSignatureRef.current = null;
      return undefined;
    }

    const toastSignature = JSON.stringify({
      message,
      error,
      highPriority,
      effectiveAutoHideDuration,
    });

    if (lastToastSignatureRef.current === toastSignature) {
      return undefined;
    }

    lastToastSignatureRef.current = toastSignature;

    const toastOptions = {
      autoClose: effectiveAutoHideDuration,
      closeOnClick: true,
      pauseOnHover: true,
      pauseOnFocusLoss: true,
      draggable: true,
    };

    error
      ? toast.error(message, toastOptions)
      : toast.success(message, toastOptions);

    onCloseRef.current?.();

    return undefined;
  }, [effectiveAutoHideDuration, error, highPriority, isOpen, message]);

  return null;
};

export default SuccessPopup;

SuccessPopup.propTypes = {
  message: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  autoHideDuration: PropTypes.number,
  error: PropTypes.bool,
  highPriority: PropTypes.bool,
};
